<template>
  <div class="chart-widget" data-testid="chartWidget-container">
    <EmptyState
      v-if="showEmptyState"
      data-testid="chartWidget-emptyState"
      :data="emptyStateData"
    />

    <Chart
      v-else
      class="pe-6 pb-4 ps-2"
      :data="chartData"
      :height="height"
      :options="options"
      :tid="`chart-widget-${type}`"
      :type="type"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import chroma from 'chroma-js'

import Chart from '@/components/utils/Chart.vue'
import EmptyState from '@/containers/ReportsView/Widgets/EmptyState.vue'

const BASE_COLOR = '#2A316C'
const TOP_COLOR = '#B3DFC1'
const LINE_TYPE = 'line'
const BAR_TYPE = 'bar'
const BUBBLE_TYPE = 'bubble'
const RADAR_TYPE = 'radar'
const DOUGHNUT_TYPE = 'doughnut'
const TREE_TYPE = 'treemap'
const BREAK_LABELS_LENGTH = 10
const MAX_LEGEND_LABELS_TO_SHOW = 19 // 19 is the max number of legends that can be displayed in a column

export default {
  name: 'ChartWidget',

  components: {
    Chart,
    EmptyState,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      baseColorPattern: BASE_COLOR,
      topColorPattern: TOP_COLOR,
      doughnutChartOptions: {
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: {
              filter(legendItem) {
                return legendItem.index < MAX_LEGEND_LABELS_TO_SHOW
              },
            },
          },
        },
      },

      lineChartOptions: {
        plugins: {
          legend: {
            labels: {
              generateLabels(chart) {
                return chart?.data?.datasets?.map?.((dataset, index) => ({
                  datasetIndex: index,
                  text: dataset.label,
                  fillStyle: 'transparent',
                  strokeStyle: 'transparent',
                }))
              },
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: false,
            },
          },
          y: {
            display: true,
            title: {
              display: false,
            },
            beginAtZero: true,
          },
        },
      },
      barChartOptions: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: () => '',
            },
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: false,
            },
          },
          y: {
            display: true,
            title: {
              display: false,
            },
            beginAtZero: true,
          },
        },
      },
      treeChartOptions: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title() {
                return ''
              },
              label(item) {
                const dataset = item.dataset
                const dataItem = dataset.data[item.dataIndex]
                const obj = dataItem._data
                const category = obj.title
                return `${category}: ${dataItem.v}`
              },
            },
          },
        },
      },
    }
  },

  computed: {
    type() {
      return this.data?.type ?? null
    },

    height() {
      if (this.data?.chartHeight) return this.data?.chartHeight
      return this.chartData?.labels?.length > BREAK_LABELS_LENGTH
        ? '450px'
        : '300px'
    },

    propData() {
      return this.data?.data ?? {}
    },

    showEmptyState() {
      const datasets = this.propData?.datasets ?? []

      if (_.isEmpty(datasets)) return true

      return !datasets.some(dataset => {
        return this.hasDatasetValues(dataset)
      })
    },

    emptyStateData() {
      return {
        action: {},
        content: this.$t('insights.no_results_for_your_filters'),
        title: this.$t('insights.no_data'),
      }
    },

    options() {
      const dataOptions = this.data?.options ?? {}

      if (this.type === BAR_TYPE) {
        return _.merge(dataOptions, this.barChartOptions)
      }

      if (this.type === LINE_TYPE) {
        return _.merge(dataOptions, this.lineChartOptions)
      }

      if (this.type === DOUGHNUT_TYPE) {
        return _.merge(dataOptions, this.doughnutChartOptions)
      }

      if (this.type === TREE_TYPE) {
        return _.merge(dataOptions, this.treeChartOptions)
      }
      return dataOptions
    },

    chartData() {
      const isMultiDataset = this.propData?.datasets?.length > 1
      return {
        ...this.propData,
        datasets: this.propData?.datasets?.map?.((dataset, index) => {
          if (this.type === LINE_TYPE)
            return this.getLineDataset(dataset, index)

          if (
            this.type === BUBBLE_TYPE ||
            (isMultiDataset && this.type === BAR_TYPE)
          )
            return this.getBubbleOrBarMultipleDataset(dataset, index)

          if (this.type === RADAR_TYPE)
            return this.getRadarDataset(dataset, index)

          if (this.type === TREE_TYPE) {
            return this.getTreeDataset(dataset)
          }

          return this.getDefaultDataset(dataset, index)
        }),
      }
    },
  },

  methods: {
    hasDatasetValues(dataset) {
      const chartValues = dataset?.data ?? []
      const treeValues = dataset?.tree ?? []

      return !_.isEmpty(chartValues) || !_.isEmpty(treeValues)
    },

    getLineDataset(dataset, index) {
      return {
        ...dataset,
        label: '',
        borderColor: this.getColorScaleFromIndex(index),
        tension: 0.4,
        backgroundColor: 'transparent',
      }
    },

    getBubbleOrBarMultipleDataset(dataset, index) {
      return {
        ...dataset,
        backgroundColor: this.getColorScaleFromIndex(index),
      }
    },

    getRadarDataset(dataset, index) {
      const borderColor = this.getColorScaleFromIndex(index)
      return {
        ...dataset,
        borderColor,
        backgroundColor: this.getColorTransparency(borderColor),
      }
    },

    getTreeDataset(dataset) {
      return {
        ...dataset,
        backgroundColor: datasets =>
          this.getColorsScale(datasets.dataset.tree.length)[
            datasets?.dataIndex
          ],
      }
    },

    getDefaultDataset(dataset) {
      return {
        ...dataset,
        backgroundColor: this.getColorsScale(
          dataset?.data?.length || dataset?.tree?.length
        ),
      }
    },

    getColorScaleFromIndex(index) {
      const datasetsLength = this.propData?.datasets?.length
      return this.getColorsScale(datasetsLength)[index]
    },

    getColorTransparency(color) {
      return chroma(color)
        .alpha(0.3)
        .hex()
    },

    getColorsScale(dataLength) {
      return chroma
        .scale([this.baseColorPattern, this.topColorPattern])
        .mode('lch')
        .colors(dataLength)
    },
  },
}
</script>
